import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { Page, PageBody, Spinner } from '@studio/legacy-components';
import { getMobileTargetingSections } from '@studio/conditions';
import { replace as replacePage } from 'next/entities/page';
import { drop as dropContentStatus } from 'next/entities/content-status';
import {
  Shape as RuleShape,
  update as updateRule,
  selectRule,
} from 'next/entities/rules';
import { selectAccount } from 'next/entities/account';
import { selectExperience } from 'next/entities/experiences';
import { selectUser } from 'next/entities/user';
import { selectAccountUser } from 'next/entities/account-users';
import Audience from 'next/components/Audience';
import MobileHeader from 'next/components/MobileHeader';

import Prioritization from './Prioritization';
import { Loading } from './styled';
import MobileBanner from './MobileBanner';
import Trigger from './Trigger';
import Screen from './Screen';
import Details from './Details';
import SettingsHeaderActions from './SettingsHeaderActions';
import { Version } from './Version';
import Goal from './Goal';
import FlowFrequency from './FlowFrequency';
import Actions from './Actions/Actions';

export const SettingsPage = ({
  accountId,
  appId = '',
  id,
  loaded,
  rule,
  onLoad,
  onUnload,
  onRuleChange,
}) => {
  useEffect(() => {
    onLoad();
    return onUnload;
  }, [onLoad, onUnload]);

  if (!loaded) {
    return (
      <Loading>
        <Spinner aria-label="Loading" />
      </Loading>
    );
  }

  const sections = getMobileTargetingSections(rule.conditions);

  // TODO: avoid passing the branches to onChange even though they hadn't changed
  const handleSectionChange = section => (newConditions, ruleChanges) => {
    return void onRuleChange({
      ...ruleChanges,
      conditions: {
        and: Object.values({
          ...sections,
          [section]: newConditions,
        }).filter(branch => branch && (branch.and || branch.or).length > 0),
      },
    });
  };

  return (
    <Page>
      <MobileHeader
        id={id}
        currentPage="settings"
        renderActions={({ published, updatedAt, state }) => {
          return (
            <SettingsHeaderActions
              accountId={accountId}
              id={id}
              published={published}
              updatedAt={updatedAt}
              state={state}
            />
          );
        }}
      />
      <PageBody>
        <MobileBanner id={id} />
        <Details id={id} />
        <Version
          conditions={sections.version}
          onChange={handleSectionChange('version')}
        />
        <Audience
          experienceName="Flow"
          conditions={sections.audience}
          onChange={handleSectionChange('audience')}
        />
        <Trigger
          conditions={sections.trigger}
          frequency={rule.frequency}
          onChange={handleSectionChange('trigger')}
        />
        <Screen
          conditions={sections.screen}
          onChange={handleSectionChange('screen')}
        />
        <Prioritization id={id} />
        <FlowFrequency
          onRuleChange={onRuleChange}
          throttleExempt={rule.throttleExempt}
        />
        <Goal id={id} />
        <Actions
          appId={appId}
          id={id}
          nextContentId={rule.nextContentId}
          onSave={onRuleChange}
          schemeLink={rule.redirectUrl}
        />
      </PageBody>
    </Page>
  );
};

SettingsPage.propTypes = {
  accountId: PropTypes.string,
  appId: PropTypes.string,
  id: PropTypes.string,
  loaded: PropTypes.bool,
  rule: RuleShape,
  onLoad: PropTypes.func,
  onUnload: PropTypes.func,
  onRuleChange: PropTypes.func,
};

const mapStateToProps = (
  state,
  {
    match: {
      params: { experienceId },
    },
  }
) => {
  const { id: accountId } = selectAccount(state);
  const experience = selectExperience(state, experienceId);
  const rule = selectRule(state, experienceId);
  const { id: userId } = selectUser(state);
  const accountUser = selectAccountUser(state, userId);

  return {
    accountId,
    appId: experience?.appId,
    id: experienceId,
    loaded: Boolean(experience && rule && accountUser),
    rule,
  };
};

const mapDispatchToProps = (
  dispatch,
  {
    match: {
      path,
      params: { experienceId: id },
    },
  }
) => ({
  onLoad: () => dispatch(replacePage({ path, id })),
  onUnload: () => dispatch(dropContentStatus(id)),
  onRuleChange: delta => dispatch(updateRule(id, delta)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsPage);
